import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  activeOverlays,
  lensesToQuery,
  sequenceSteps,
  toggleLens
} from "./lib/overlays";
import { sequenceColour } from "./lib/palette";
import { findScale } from "./lib/colorScales";

// Below this width a 4-unit SVG label renders around 9px, so the sphere names
// drop and only the numerals remain. The names live in the detail panel.
export const NAME_BREAKPOINT = 640;

// Below this width twenty-two labelled lines are unreadable, the table becomes
// the primary view, and the lens picker becomes a bottom sheet rather than five
// channel groups crammed into a phone sidebar.
export const RAIL_BREAKPOINT = 900;

function useWidth(ref) {
  const [width, setWidth] = useState(RAIL_BREAKPOINT);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}

// EVERYTHING THE TREE KNOWS, AND NOTHING IT DRAWS.
//
// `TreeOfLife.jsx` was 619 lines and one function: fourteen `useState` calls,
// thirteen derivations and two effects, and only then three hundred lines of
// markup. That is the shape every other island here grew out of — the atlas
// keeps its state in `useAtlas`, the geomantic stage counts its beats in
// `useBeat` — and the Tree was the one that never did.
//
// The split is at the seam those files already draw: what is true of the tree
// lives here, what it LOOKS like lives in the component. Nothing in this file
// renders anything, and nothing in the component works anything out.
//
// It also normalises the payload. `overlays`, `channelParams` and
// `multiChannels` carry defaults, and when the component destructured `tree`
// for itself as well those defaults existed in two places and were free to
// disagree — so they are applied once, here, and handed back.
export default function useTreeState(tree) {
  const {
    tradition,
    availableTraditions,
    sephiroth,
    paths,
    viewBox,
    overlays = [],
    channelParams = {},
    multiChannels = ["marker"]
  } = tree;

  // ONE selection value. Never two.
  const [selected, setSelected] = useState(tree.selection || null);
  // One entry per visual channel. Two overlays on a channel cannot coexist.
  const [lenses, setLenses] = useState(tree.lenses || {});
  const [step, setStep] = useState(tree.step || 1);
  // Tree | Table. Explicit and shareable; never overridden by a breakpoint.
  const [view, setView] = useState(tree.view || "tree");
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState("1");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(Boolean(tree.about));
  const [copied, setCopied] = useState(false);

  // FIVE SCALES OR NONE, AND THE PAYLOAD DECIDES WHICH. `scale` is the slug the
  // reader chose; `scale` resolved through `findScale` is the descriptor that
  // says which column the spheres read and which the paths read. The Minutum
  // Mundum is the only one where those differ, which is exactly why the two
  // used to be separate pieces of state — a sphere scale in the control and a
  // path scale nailed to the tradition's default, with no way to move the paths
  // at all.
  const scales = tradition.colorScales || [];
  const [scaleSlug, setScaleSlug] = useState(
    tree.colorScale || tradition.defaultColorScale || "minutum_mundum"
  );
  const scale = useMemo(() => findScale(scales, scaleSlug), [scales, scaleSlug]);
  const setScale = useCallback((next) => setScaleSlug(next), []);
  const [sphereInscription, setSphereInscription] = useState("number");
  // A node that is drawn but not counted among the ten — Da'at on the Jewish
  // tree — can be taken off the diagram. It stays in the table either way,
  // where a row is a row and the count is stated in the caption.
  const [showUncounted, setShowUncounted] = useState(true);

  const rootRef = useRef(null);
  const width = useWidth(rootRef);
  const showNames = width >= NAME_BREAKPOINT;
  const wide = width >= RAIL_BREAKPOINT;

  const sephirothBySlug = useMemo(
    () => new Map(sephiroth.map((s) => [s.slug, s])),
    [sephiroth]
  );

  // The uncounted nodes, and whether this tradition has any. The Hermetic tree
  // has none: its Da'ath is a marker lens, not a sephirah, which is the whole
  // argument that lens exists to make.
  const uncounted = useMemo(
    () => sephiroth.filter((s) => s.countedInTen === false),
    [sephiroth]
  );
  // The DIAGRAM's list. `sephirothBySlug` above stays complete, so an overlay
  // that walks through Da'at still resolves it while it is off the drawing.
  const drawnSephiroth = useMemo(
    () => (showUncounted ? sephiroth : sephiroth.filter((s) => s.countedInTen !== false)),
    [sephiroth, showUncounted]
  );
  const pathsBySlug = useMemo(() => new Map(paths.map((p) => [p.slug, p])), [paths]);

  const active = useMemo(
    () => activeOverlays(overlays, lenses, multiChannels),
    [overlays, lenses, multiChannels]
  );
  const sequenceOverlay = useMemo(
    () => active.find((o) => o.kind === "sequence") || null,
    [active]
  );
  const shapeOverlays = useMemo(
    () => active.filter((o) => o.kind !== "sequence"),
    [active]
  );
  const regionOverlay = useMemo(
    () => active.find((o) => o.channel === "region") || null,
    [active]
  );
  const legendOverlays = useMemo(
    () => shapeOverlays.filter((overlay) => overlay.channel !== "region"),
    [shapeOverlays]
  );

  // A lens may declare what goes inside the spheres, and for as long as it is
  // on it takes the control over rather than fighting it. DERIVED, not synced:
  // copying the lens's value into the state would leave the reader's own
  // choice overwritten after they turn the lens off again.
  const inscriptionLens = useMemo(
    () => active.find((overlay) => overlay.sphereInscription) || null,
    [active]
  );
  const inscription = inscriptionLens
    ? inscriptionLens.sphereInscription
    : sphereInscription;

  const steps = useMemo(() => {
    if (!sequenceOverlay) return [];
    const all = sequenceSteps(sequenceOverlay, sephirothBySlug, pathsBySlug);
    return all.map((s, i, arr) => ({
      ...s,
      // "Step 4 of 10. Binah to Chesed. No path — the Abyss."
      label: [
        s.node && s.kind === "sephirah" && i > 0
          ? `${arr[i - 1].node ? arr[i - 1].node.name : ""} to ${s.node.name}.`
          : null,
        s.kind === "sephirah" && s.segmentHasPath === false && paths.length
          ? "No path — the Abyss."
          : null,
        s.note ||
          (s.node
            ? s.kind === "sephirah"
              ? s.node.name
              : `${s.node.letterName}: ${s.node.fromName} to ${s.node.toName}`
            : s.slug)
      ]
        .filter(Boolean)
        .join(" ")
    }));
  }, [sequenceOverlay, sephirothBySlug, pathsBySlug, paths.length]);

  const clampedStep = Math.min(Math.max(step, 1), Math.max(steps.length, 1));

  // Selecting the lens lights the WHOLE descent: all ten sephiroth and every
  // path it runs along. The step moves a cursor within that, it does not gate
  // it — a lens that reveals itself one node at a time shows you almost nothing
  // until you have clicked nine times.
  const sequenceState = useMemo(() => {
    if (!sequenceOverlay || steps.length === 0) return null;

    const spheres = new Set();
    const involvedPaths = new Set();

    steps.forEach((entry, index) => {
      if (entry.kind === "sephirah") {
        spheres.add(entry.slug);
        // The segment ARRIVING at this step. Null where there is no path
        // beneath it — that one is drawn dashed instead.
        if (index > 0 && entry.segmentPathSlug) involvedPaths.add(entry.segmentPathSlug);
      } else {
        involvedPaths.add(entry.slug);
        const path = pathsBySlug.get(entry.slug);
        if (path) {
          spheres.add(path.fromSlug);
          spheres.add(path.toSlug);
        }
      }
    });

    // Ring ONLY the current step's endpoints. Ringing all ten made every sphere
    // look selected, so selection meant nothing. The lit paths still show the
    // whole shape of the sequence.
    const current = steps[clampedStep - 1];
    const ringed = new Set();
    if (current) {
      if (current.kind === "sephirah") {
        ringed.add(current.slug);
        const previous = steps[clampedStep - 2];
        if (previous && previous.kind === "sephirah") ringed.add(previous.slug);
      } else if (current.node) {
        ringed.add(current.node.fromSlug);
        ringed.add(current.node.toSlug);
      }
    }

    return {
      spheres,
      ringed,
      paths: involvedPaths,
      currentSphereSlug: current && current.kind === "sephirah" ? current.slug : null,
      currentPathSlug: current && current.kind === "path" ? current.slug : null,
      colour: sequenceColour(sequenceOverlay)
    };
  }, [sequenceOverlay, steps, clampedStep, pathsBySlug]);

  const selectedNode = useMemo(() => {
    if (!selected) return null;
    // A marker is only resolvable while its lens is on. Turn the Veils off with
    // Ain Soph Aur selected and the panel goes back to the lens commentary
    // rather than describing something that is no longer drawn.
    if (selected.type === "marker") {
      // "marker" is the selection value for AN OVERLAY GROUP, and a region band
      // is one as much as a veil is. Both channels are searched through the one
      // value rather than a fourth type being grown beside the three.
      const overlay = active.find(
        (candidate) =>
          (candidate.channel === "marker" || candidate.channel === "region") &&
          candidate.groups.some((group) => group.slug === selected.slug)
      );
      if (!overlay) return null;
      const data = overlay.groups.find((group) => group.slug === selected.slug);
      return data ? { type: "marker", data: { ...data, channel: overlay.channel } } : null;
    }
    // Resolved against what is DRAWN: hiding Da'at while its panel is open
    // should close the panel, not leave a reader reading about a sphere that
    // is no longer on the diagram.
    const list = selected.type === "sephirah" ? drawnSephiroth : paths;
    const data = list.find((n) => n.slug === selected.slug);
    return data ? { type: selected.type, data } : null;
  }, [selected, drawnSephiroth, paths, active]);

  const handleSelect = useCallback((node) => {
    setSelected(node && node.slug ? { type: node.type, slug: node.slug } : null);
  }, []);

  // The two setState calls stay OUTSIDE the updater. React may invoke an
  // updater more than once for the same change (StrictMode does exactly that),
  // so an updater that also dispatches is an updater that fires its side effect
  // twice — and `toggleLens` is pure, which is the whole reason it is testable.
  const handleToggleLens = useCallback(
    (overlay) => {
      setLenses((current) => toggleLens(current, overlay, multiChannels));
      if (overlay.kind === "sequence") {
        setStep(1);
        setPlaying(false);
      }
    },
    [multiChannels]
  );

  // Turning lenses off stops a sequence as well as hiding its transport, or the
  // play state survives with nothing left to play.
  const clearLenses = useCallback(() => {
    setLenses({});
    setPlaying(false);
  }, []);

  const clearChannel = useCallback((channel) => {
    setLenses((current) => {
      const next = { ...current };
      delete next[channel];
      return next;
    });
    setPlaying(false);
  }, []);

  // Clamped here rather than at the control, so every caller of it — the
  // transport's two buttons today, anything else later — gets the same bounds.
  const goToStep = useCallback(
    (next) => setStep(Math.min(Math.max(next, 1), steps.length)),
    [steps.length]
  );

  const activeCount = Object.values(lenses).reduce(
    (total, value) => total + (Array.isArray(value) ? value.length : 1),
    0
  );

  // Lens state lives in the URL so a specific view — including a specific
  // moment of a sequence — is linkable.
  useEffect(() => {
    if (typeof window === "undefined" || !window.history) return;
    const params = lensesToQuery(lenses, channelParams, multiChannels);
    if (sequenceOverlay && clampedStep > 1) params.set("step", String(clampedStep));
    if (view !== "tree") params.set("view", view);
    if (aboutOpen) params.set("about", "1");
    // The scale is in the address for the reason a lens is: it is part of what
    // the diagram SAYS. The default is left out, so the plain URL stays plain.
    if (scales.length > 0 && scaleSlug !== tradition.defaultColorScale) {
      params.set("scale", scaleSlug);
    }
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ""}`;
    window.history.replaceState({}, "", url);
    // The address just copied is no longer the address in the bar, so the offer
    // to copy it goes back to being an offer.
    setCopied(false);
  }, [lenses, clampedStep, sequenceOverlay, view, aboutOpen, channelParams, multiChannels,
      scaleSlug, scales.length, tradition.defaultColorScale]);

  // Escape clears the selection from anywhere on the page.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelected(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const copyLink = useCallback(() => {
    if (typeof window === "undefined") return;
    const url = window.location.href;
    const done = () => setCopied(true);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(done, done);
    } else {
      done();
    }
  }, []);

  return {
    // The payload, normalised once — these carry defaults and must not be
    // destructured a second time anywhere else.
    availableTraditions,
    channelParams,
    multiChannels,
    overlays,
    paths,
    sephiroth,
    tradition,
    viewBox,
    // What the container measures, and the two layout answers that follow.
    rootRef,
    showNames,
    wide,
    // Selection: one value, resolved against what is drawn.
    handleSelect,
    selected,
    setSelected,
    selectedNode,
    // Lenses, and the overlays they resolve to.
    active,
    activeCount,
    clearChannel,
    clearLenses,
    handleToggleLens,
    legendOverlays,
    lenses,
    regionOverlay,
    sequenceOverlay,
    shapeOverlays,
    // What goes inside the spheres. `inscription` is the effective answer; the
    // lens wins while it is on.
    inscription,
    inscriptionLens,
    setSphereInscription,
    // The sequence, and its transport.
    goToStep,
    playing,
    sequenceState,
    setPlaying,
    setSpeed,
    speed,
    step: clampedStep,
    steps,
    // Tree | Table, and the chrome around it.
    aboutOpen,
    copied,
    copyLink,
    setAboutOpen,
    setSheetOpen,
    setView,
    sheetOpen,
    view,
    // The spheres themselves: which are drawn, how they are coloured, and the
    // ones this tradition draws without counting.
    drawnSephiroth,
    pathsBySlug,
    scale,
    scaleSlug,
    scales,
    sephirothBySlug,
    setScale,
    setShowUncounted,
    showUncounted,
    uncounted
  };
}
